/* heroCore.jsx
 * Shared logic for the 3 hero variants.
 * Ported 1:1 from "Hero A - dev 2/hero-core.jsx" (CDN+Babel handoff) to ESM for CRA.
 * Exports (named ESM):
 *   CATALOG, MARCAS, modelosOf, versionsOf, tagsOf, YEARS,
 *   fmtMXN, calcMonthly, getCarroceria, getMotor, parseBudget,
 *   aiSuggest, aiNarrate, suggestionsFor, PLACEHOLDERS,
 *   useAI, useTypewriter, useCountUp,
 *   Ico, IconForSug, KW, matchVehicle, matchVehicleTags, matchPlan, matchPlazo, matchEngPct
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { fireQuoteConversion } from './conversions';

/* ── Catalog (subset of the production catalog, with type tags) ────────── */
export const CATALOG = {
  Toyota: {
    Corolla: { tags: ['compacto', 'sedan', 'familiar', 'eficiente', 'híbrido', 'ciudad'], versions: [['LE Aut', 419000], ['SE Aut', 469000], ['XLE Aut', 519000]] },
    RAV4:    { tags: ['suv', 'familiar', 'híbrido', 'viajes', '5 personas'],          versions: [['LE', 629000], ['XLE', 709000]] },
    Hilux:   { tags: ['pickup', 'trabajo', 'carga', '4x4', 'resistente'],             versions: [['SR', 589000], ['SRV 4x4', 739000]] },
  },
  Nissan: {
    Versa:  { tags: ['compacto', 'sedan', 'económico', 'ciudad'],         versions: [['Sense', 309000], ['Advance', 359000]] },
    Sentra: { tags: ['sedan', 'familiar', 'eficiente'],                   versions: [['Sense', 419000], ['Advance', 469000]] },
    Kicks:  { tags: ['suv', 'compacto', 'urbano', 'ciudad'],              versions: [['Sense', 419000], ['Advance', 479000]] },
  },
  Mazda: {
    Mazda3: { tags: ['sedan', 'deportivo', 'compacto', 'elegante', 'premium'], versions: [['i Sport', 429000], ['i Grand Touring', 519000]] },
    CX30:   { tags: ['suv', 'compacto', 'urbano', 'elegante'],                  versions: [['i Sport', 489000], ['i Grand Touring', 569000]] },
    CX5:    { tags: ['suv', 'familiar', 'premium', 'viajes'],                   versions: [['i Sport', 569000], ['Signature', 689000]] },
  },
  Hyundai: {
    Accent: { tags: ['compacto', 'económico', 'ciudad', 'eficiente'],   versions: [['GL', 339000], ['GLS', 399000]] },
    Creta:  { tags: ['suv', 'compacto', 'familiar', 'urbano'],          versions: [['GL', 429000], ['Limited', 519000]] },
    Tucson: { tags: ['suv', 'familiar', '5 personas', 'viajes'],        versions: [['GLS', 579000], ['Limited', 709000]] },
  },
  Chirey: {
    'Tiggo 7 Pro': { tags: ['suv', 'familiar', 'premium', 'híbrido'],  versions: [['Comfort L4 Aut', 558000], ['Luxury L4 Aut', 658000]] },
    'Tiggo 8 Pro': { tags: ['suv', 'grande', '7 personas', 'familiar'], versions: [['Comfort', 689000], ['Luxury', 789000]] },
  },
};
export const MARCAS = Object.keys(CATALOG);
export const modelosOf = (m) => CATALOG[m] ? Object.keys(CATALOG[m]) : [];
export const versionsOf = (m, mod) => (CATALOG[m] && CATALOG[m][mod]) ? CATALOG[m][mod].versions : [];
export const tagsOf     = (m, mod) => (CATALOG[m] && CATALOG[m][mod]) ? CATALOG[m][mod].tags : [];
export const YEARS = ['2026', '2025', '2024'];

export const fmtMXN = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('es-MX');

/* Body-type & motor lookup */
export function getCarroceria(marca, modelo) {
  const t = tagsOf(marca, modelo);
  if (t.includes('pickup')) return 'pickup';
  if (t.includes('suv'))    return 'suv';
  return 'sedan';
}
export function getMotor(marca, modelo, prompt) {
  const t = tagsOf(marca, modelo);
  const hasHybrid = t.includes('híbrido') || t.includes('hibrido');
  if (prompt && /gasolin/i.test(prompt)) return 'gasolina';
  return hasHybrid ? 'híbrido' : 'gasolina';
}

/* Budget parsing ("$7,000 al mes" / "presupuesto $300k") */
export function parseBudget(prompt) {
  if (!prompt) return { monthly: null, total: null };
  const p = prompt.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ');
  let monthly = null, total = null;
  const m1 = p.match(/\$?\s*(\d+(?:\.\d+)?)\s*(mil|k)?\s*(?:al mes|mensual(?:es)?|por mes|x mes|\/mes|de mensualidad)/);
  if (m1) {
    let v = parseFloat(m1[1]);
    if (m1[2] || v < 100) v *= 1000;
    monthly = v;
  }
  const m2 = p.match(/(?:presupuesto|m[áa]ximo|hasta|tengo|cuento con)\s*(?:de\s*)?\$?\s*(\d+(?:\.\d+)?)\s*(mil|k)?/);
  if (m2) {
    let v = parseFloat(m2[1]);
    if (m2[2] || v < 1000) v *= 1000;
    if (v >= 100000) total = v;
  }
  return { monthly, total };
}

export function matchVehicleTags(prompt) {
  const p = (prompt || '').toLowerCase();
  const out = new Set();
  for (const k of Object.keys(KW)) if (p.includes(k)) out.add(k);
  return Array.from(out);
}

/* ── Rough leasing math (illustrative, matches the vibe of the live calc) */
export function calcMonthly({ precio, enganche, plazo, plan }) {
  if (!precio || !plazo) return 0;
  const financed = Math.max(0, precio - (enganche || 0));
  const rate = plan === 'puro' ? 1.18 : 1.21; // pure-lease slightly cheaper monthly
  const base = (financed * rate) / plazo;
  // seguros + asistencia incluidos (~ 800/mes)
  return Math.round(base + 800);
}

/* ── Intent → vehicle matcher (mock AI brain) ──────────────────────────── */
export const KW = {
  // body type
  pickup:    ['Toyota', 'Hilux'],
  suv:       ['Mazda', 'CX5'],
  compacto:  ['Hyundai', 'Accent'],
  sedan:     ['Toyota', 'Corolla'],
  familiar:  ['Toyota', 'RAV4'],
  grande:    ['Chirey', 'Tiggo 8 Pro'],
  // family / passengers
  familia:   ['Toyota', 'RAV4'],
  esposa:    ['Toyota', 'Corolla'],
  hijos:     ['Hyundai', 'Tucson'],
  '5 personas': ['Toyota', 'RAV4'],
  '7 personas': ['Chirey', 'Tiggo 8 Pro'],
  // style / budget
  económico: ['Hyundai', 'Accent'],
  barato:    ['Nissan', 'Versa'],
  eficiente: ['Toyota', 'Corolla'],
  ahorro:    ['Hyundai', 'Accent'],
  premium:   ['Mazda', 'CX5'],
  lujo:      ['Mazda', 'CX5'],
  elegante:  ['Mazda', 'Mazda3'],
  deportivo: ['Mazda', 'Mazda3'],
  // tech
  híbrido:   ['Toyota', 'RAV4'],
  hibrido:   ['Toyota', 'RAV4'],
  // use
  trabajo:   ['Toyota', 'Hilux'],
  carga:     ['Toyota', 'Hilux'],
  ciudad:    ['Nissan', 'Kicks'],
  urbano:    ['Nissan', 'Kicks'],
  viajes:    ['Toyota', 'RAV4'],
};

export function matchVehicle(prompt) {
  const p = (prompt || '').toLowerCase();
  let hit = null;
  for (const k of Object.keys(KW)) {
    if (p.includes(k)) { hit = KW[k]; break; }
  }
  if (!hit) hit = ['Toyota', 'Corolla']; // safe default
  const [marca, modelo] = hit;
  // version: premium/lujo → top trim; barato/económico → bottom; else mid
  const vs = versionsOf(marca, modelo);
  let vIdx = 0;
  if (/premium|lujo|elegante|alto/.test(p))      vIdx = vs.length - 1;
  else if (/barato|económico|economico/.test(p)) vIdx = 0;
  else                                             vIdx = Math.min(1, vs.length - 1);
  return { marca, modelo, vIdx };
}

export function matchPlan(prompt) {
  const p = (prompt || '').toLowerCase();
  if (/(deduc|impuest|factur|negoci|empresa|pfae|moral|fiscal)/.test(p)) return 'puro';
  return 'financiero';
}

export function matchPlazo(prompt) {
  const p = (prompt || '').toLowerCase();
  const m = p.match(/(\d{2})\s*(mes|més|meses)/);
  if (m) {
    const n = parseInt(m[1], 10);
    if ([24,36,48,60].includes(n)) return n;
  }
  if (/(corto|rápido|rapid)/.test(p)) return 36;
  if (/(largo|cómodo|comod)/.test(p)) return 60;
  return 48;
}

export function matchEngPct(prompt) {
  const p = (prompt || '').toLowerCase();
  if (/(sin enganche|poco enganche|mínim|minim)/.test(p)) return .10;
  if (/(alto enganche|fuerte enganche|grande enganche)/.test(p)) return .30;
  return .20;
}

export function aiSuggest(prompt) {
  const budget = parseBudget(prompt);
  let veh = matchVehicle(prompt);

  // If a total budget was given, find best-value model whose precio <= budget.
  if (budget.total) {
    const matchedTags = new Set(matchVehicleTags(prompt));
    const candidates = [];
    for (const [m, models] of Object.entries(CATALOG)) {
      for (const [mod, data] of Object.entries(models)) {
        data.versions.forEach((v, i) => {
          if (v[1] <= budget.total) {
            const overlap = data.tags.filter(t => matchedTags.has(t)).length;
            candidates.push({ marca: m, modelo: mod, vIdx: i, precio: v[1], overlap });
          }
        });
      }
    }
    if (candidates.length) {
      candidates.sort((a, b) => (b.overlap - a.overlap) || (b.precio - a.precio));
      veh = { marca: candidates[0].marca, modelo: candidates[0].modelo, vIdx: candidates[0].vIdx };
    }
  }

  const plan = matchPlan(prompt);
  const versions = versionsOf(veh.marca, veh.modelo);
  const [version, precio] = versions[veh.vIdx] || ['', 0];
  let plazo = matchPlazo(prompt);
  let engPct = matchEngPct(prompt);

  // If a monthly budget was given, search a (plazo, engPct) combo to hit it.
  if (budget.monthly && precio) {
    let best = null;
    for (const pl of [60, 48, 36, 24]) {
      for (const ep of [.10, .15, .20, .25, .30, .40]) {
        const eng = Math.round((precio * ep) / 1000) * 1000;
        const m = calcMonthly({ precio, enganche: eng, plazo: pl, plan });
        const d = Math.abs(m - budget.monthly);
        if (!best || d < best.d) best = { d, pl, ep };
      }
    }
    if (best) { plazo = best.pl; engPct = best.ep; }
  }

  const enganche = Math.round(precio * engPct); // monto exacto (como useCarCalculator), sin redondear a miles
  const motor = getMotor(veh.marca, veh.modelo, prompt);
  const carroceria = getCarroceria(veh.marca, veh.modelo);
  return { ...veh, version, precio, year: '2026', plan, plazo, enganche, engPct, motor, carroceria, budget };
}

/* ── Thought stream sentence ──────────────────────────────────────────── */
export function aiNarrate(prompt, sol) {
  if (!prompt) return '';
  const reasons = [];
  const p = prompt.toLowerCase();
  if (sol.budget && sol.budget.monthly)        reasons.push('tu presupuesto de ' + fmtMXN(sol.budget.monthly) + '/mes');
  if (sol.budget && sol.budget.total)          reasons.push('tope de ' + fmtMXN(sol.budget.total));
  if (/familia|esposa|hijos|familiar/.test(p)) reasons.push('mencionas familia');
  if (/pickup|trabajo|carga/.test(p))           reasons.push('necesitas espacio de carga');
  if (/compacto|peque|ciudad|urbano/.test(p))   reasons.push('priorizas la ciudad');
  if (/híbrid|hibrid|eficiente|ahorro/.test(p)) reasons.push('quieres eficiencia');
  if (/premium|lujo|elegante/.test(p))          reasons.push('buscas algo premium');
  if (/deduc|impuest|negoci|empresa/.test(p))   reasons.push('quieres deducir impuestos');

  const because = reasons.length
    ? 'Como ' + reasons.slice(0, 3).join(' y ') + ', te propongo el '
    : 'Te propongo el ';
  const planTxt = sol.plan === 'puro'
    ? 'Plan Puro (deducible 100%)'
    : 'Plan Financiero';
  return because + sol.marca + ' ' + sol.modelo + ' ' + sol.version
    + ' · ' + planTxt + ' a ' + sol.plazo + ' meses, con ' + Math.round(sol.engPct*100) + '% de enganche.';
}

/* ── Typewriter placeholder ───────────────────────────────────────────── */
export function useTypewriter(words, { speed = 55, pause = 1600, enabled = true } = {}) {
  const [out, setOut] = useState('');
  const [i, setI] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const w = words[i % words.length];
    let t;
    if (!del && out.length < w.length) {
      t = setTimeout(() => setOut(w.slice(0, out.length + 1)), speed);
    } else if (!del && out.length === w.length) {
      t = setTimeout(() => setDel(true), pause);
    } else if (del && out.length > 0) {
      t = setTimeout(() => setOut(w.slice(0, out.length - 1)), speed / 2);
    } else if (del && out.length === 0) {
      setDel(false); setI(i + 1);
    }
    return () => clearTimeout(t);
  }, [out, del, i, words, speed, pause, enabled]);
  return out;
}

/* ── AI hook ──────────────────────────────────────────────────────────── */
/* suggestRef (F4): ref opcional cuyo .current es un suggester ASYNC real
 * (backend LLM + resolución RODAC, ver useHeroQuote.aiSuggestReal). Si no
 * existe o falla, cae al mock aiSuggest — el pipeline visual es el mismo. */
export function useAI({ onField, onDone, suggestRef } = {}) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy]     = useState(false);
  const [thought, setThought] = useState('');
  const [done, setDone] = useState(false);
  const cancelRef = useRef(null);

  const reset = useCallback(() => {
    if (cancelRef.current) cancelRef.current = null;
    setBusy(false); setThought(''); setDone(false);
  }, []);

  const run = useCallback(async (text) => {
    const p = (text == null ? prompt : text).trim();
    if (!p) return;
    // Conversión Google Ads: el usuario envió su descripción a Aria = cotizó (IA).
    fireQuoteConversion();
    setBusy(true); setDone(false); setThought('');

    const pendingToken = { cancelled: false };
    cancelRef.current = pendingToken;

    let sol = null;
    if (suggestRef?.current) {
      setThought('Aria está construyendo lo mejor para ti…');
      try { sol = await suggestRef.current(p); } catch (e) { sol = null; }
      if (pendingToken.cancelled) return;
      setThought('');
    }
    if (!sol) sol = aiSuggest(p);
    const narrative = sol.narrative || aiNarrate(p, sol);

    // Stream the narrative char-by-char
    const token = pendingToken;
    const tot = narrative.length;
    const stepMs = Math.max(8, Math.min(20, 1400 / Math.max(1, tot)));
    for (let i = 1; i <= tot; i++) {
      if (token.cancelled) return;
      setThought(narrative.slice(0, i));
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, stepMs));
    }
    // After narrative, sequence field fills
    const sequence = [
      ['marca',      sol.marca],
      ['modelo',     sol.modelo],
      ['year',       sol.year],
      ['version',    sol.version],
      ['precio',     sol.precio],
      ['carroceria', sol.carroceria],
      ['motor',      sol.motor],
      ['plan',       sol.plan],
      ['plazo',      sol.plazo],
      ['enganche',   sol.enganche],
    ];
    // Slower, more felt pacing so the calculator builds field-by-field.
    const delays = {
      marca: 420, modelo: 320, year: 200, version: 420, precio: 240,
      carroceria: 360, motor: 220, plan: 420, plazo: 420, enganche: 440,
    };
    for (let i = 0; i < sequence.length; i++) {
      if (token.cancelled) return;
      const [k, v, extra] = sequence[i];
      onField && onField(k, v, extra);
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, delays[k] || 400));
    }
    setBusy(false); setDone(true);
    onDone && onDone(sol);
  }, [prompt, onField, onDone, suggestRef]);

  const cancel = () => {
    if (cancelRef.current) cancelRef.current.cancelled = true;
    setBusy(false);
  };

  return { prompt, setPrompt, busy, thought, done, run, cancel, reset };
}

/* ── Suggestion chips per prompt context ──────────────────────────────── */
export function suggestionsFor(text) {
  const t = (text || '').toLowerCase();
  const base = [
    { label: 'Tengo $7,000 al mes',      icon: 'wallet' },
    { label: 'Presupuesto máximo $300k', icon: 'wallet' },
    { label: 'Familiar híbrido para 5',  icon: 'family' },
    { label: 'Pickup de trabajo',        icon: 'truck' },
    { label: 'Compacto eficiente',       icon: 'leaf' },
  ];
  if (/familia|hijos|esposa/.test(t)) {
    return [
      { label: 'Híbrido para mi familia',     icon: 'family' },
      { label: 'SUV para 5 personas',         icon: 'family' },
      { label: 'Familiar de 7 plazas',        icon: 'family' },
      { label: 'Compacto familiar económico', icon: 'leaf' },
    ];
  }
  if (/trabajo|carga|pickup/.test(t)) {
    return [
      { label: 'Pickup 4x4 resistente',    icon: 'truck' },
      { label: 'Pickup para deducir',      icon: 'truck' },
      { label: 'Pickup uso mixto',         icon: 'truck' },
      { label: 'SUV para carga',           icon: 'star' },
    ];
  }
  if (/deduc|impuest|negoci|empresa|fiscal/.test(t)) {
    return [
      { label: 'Auto para mi empresa',          icon: 'briefcase' },
      { label: 'SUV deducible 100%',            icon: 'briefcase' },
      { label: 'Plan Puro a 60 meses',          icon: 'briefcase' },
      { label: 'Pickup para mi negocio',        icon: 'truck' },
    ];
  }
  if (t.length === 0) return base;
  return base;
}

export const PLACEHOLDERS = [
  'Tengo $7,000 al mes, busco algo híbrido…',
  'Mi presupuesto máximo es $300k para algo familiar…',
  'Necesito una pickup 4x4 para trabajar en obra…',
  'Un SUV para viajar con mis hijos, eficiente…',
  'Algo deducible para mi empresa, elegante…',
];

/* ── Icons ────────────────────────────────────────────────────────────── */
export const Ico = {
  Spark:  (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 3l2.2 5.8L20 11l-5.8 2.2L12 19l-2.2-5.8L4 11l5.8-2.2L12 3z" fill="currentColor"/></svg>),
  Arrow:  (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  Send:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M14 7l5 5-5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  Mic:    (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="9" y="3" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="2"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  Check:  (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  Family: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.6"/><circle cx="16" cy="8" r="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 21c0-3 2-5 5-5s5 2 5 5M13 21c0-2 1.5-4 3.5-4s3.5 2 3.5 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>),
  Truck:  (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 16V7h10v9M13 10h4l3 3v3h-7" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><circle cx="7" cy="17" r="2" stroke="currentColor" strokeWidth="1.6"/><circle cx="17" cy="17" r="2" stroke="currentColor" strokeWidth="1.6"/></svg>),
  Leaf:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M20 4s-1 9-7 12-9-1-9-1 1-9 7-12 9 1 9 1zM5 19l9-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  Star:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 3l2.6 6 6.4.6-5 4.5 1.6 6.4L12 17l-5.6 3.5 1.6-6.4-5-4.5 6.4-.6L12 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>),
  Brief:  (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.6"/></svg>),
  Wallet: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 7a2 2 0 0 1 2-2h12v4H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="1.6"/><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7H5" stroke="currentColor" strokeWidth="1.6"/><circle cx="17" cy="13.5" r="1.2" fill="currentColor"/></svg>),
  Sparkle:(p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 2v6m0 8v6M2 12h6m8 0h6M5 5l4 4m6 6l4 4m0-14l-4 4m-6 6l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>),
  Calc:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="4" y="3" width="16" height="18" rx="3" stroke="currentColor" strokeWidth="1.6"/><rect x="7" y="6" width="10" height="4" rx="1" stroke="currentColor" strokeWidth="1.6"/><circle cx="8.5" cy="14" r="1" fill="currentColor"/><circle cx="12" cy="14" r="1" fill="currentColor"/><circle cx="15.5" cy="14" r="1" fill="currentColor"/><circle cx="8.5" cy="17.5" r="1" fill="currentColor"/><circle cx="12" cy="17.5" r="1" fill="currentColor"/><circle cx="15.5" cy="17.5" r="1" fill="currentColor"/></svg>),
  Edit:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M4 20h4l10-10-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>),
};

export function IconForSug(name, props) {
  const map = { family: Ico.Family, truck: Ico.Truck, leaf: Ico.Leaf, star: Ico.Star, briefcase: Ico.Brief, wallet: Ico.Wallet };
  const Cmp = map[name] || Ico.Spark;
  return <Cmp {...props} />;
}

/* ── Count-up animated number ─────────────────────────────────────────── */
export function useCountUp(target, ms = 800) {
  const [n, setN] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;
    startRef.current = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - startRef.current) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return n;
}
